import AddIcon from '@mui/icons-material/Add'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { unlocksAtTier } from '../../shared/layer-stack'
import { moveSwatch } from '../../shared/swatch-list'
import type { BoSection } from '../../shared/types'
import AddTile from './AddTile'

/** What the ⋮ menu on a swatch can do. The option grid's menu has the same two verbs. */
export type SwatchAction = 'retire' | 'restore'

export interface SwatchGridProps {
  section: BoSection
  /** Include the withdrawn swatches. Off, they are hidden, exactly like retired options. */
  showRetired?: boolean
  onAdd?: () => void
  onReorder?: (
    swatches: string[],
    moved: string,
    direction: 'earlier' | 'later',
  ) => void
  onSwatchAction?: (hex: string, action: SwatchAction) => void
}

export default function SwatchGrid({
  section,
  showRetired = false,
  onAdd,
  onReorder,
  onSwatchAction,
}: SwatchGridProps) {
  const { formatMessage } = useIntl()
  const [menu, setMenu] = useState<{ el: HTMLElement; hex: string } | null>(
    null,
  )
  const swatches = section.swatches ?? []
  /**
   * Compared UPPER-CASE, with no `#`. The palette and the withdrawal list are both operator
   * input, so `1d6b37` and `1D6B37` reach this component as the same colour written two ways.
   */
  const retired = new Set(
    (section.retiredSwatches ?? []).map((hex) =>
      hex.replace(/^#/, '').toUpperCase(),
    ),
  )
  const isRetired = (hex: string) =>
    retired.has(hex.replace(/^#/, '').toUpperCase())

  if (swatches.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 520 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle1">
            {formatMessage({ id: 'avatarStudio.grid.noSwatches.title' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'avatarStudio.grid.noSwatches.body' })}
          </Typography>
          {onAdd && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              sx={{ alignSelf: 'flex-start' }}
              onClick={onAdd}
            >
              {formatMessage({ id: 'avatarStudio.grid.addColour' })}
            </Button>
          )}
        </Stack>
      </Paper>
    )
  }

  const choose = (action: SwatchAction) => {
    const hex = menu?.hex
    setMenu(null)
    if (hex && onSwatchAction) {
      onSwatchAction(hex, action)
    }
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      }}
      data-testid="swatch-grid"
    >
      {swatches.map((swatch, index) => {
        // The tier a swatch unlocks at is POSITIONAL, and the API counts active swatches only -
        // so the position asked about here excludes the withdrawn ones, or the badge on screen
        // disagrees with the tier the player actually gets.
        const activeIndex = swatches
          .slice(0, index)
          .filter((s) => !isRetired(s)).length
        const withdrawn = isRetired(swatch)
        const unlocksAt = withdrawn
          ? null
          : unlocksAtTier(section.tierRule, activeIndex, swatch)

        if (withdrawn && !showRetired) {
          return null
        }

        return (
          <Paper
            key={`${swatch}-${index}`}
            variant="outlined"
            sx={{ p: 1.5, ...(withdrawn ? { opacity: 0.6 } : {}) }}
            data-testid={`swatch-${swatch}`}
            data-retired={withdrawn ? 'true' : undefined}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={0.25} alignItems="center">
                {onReorder && !withdrawn && (
                  <>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      aria-label={formatMessage(
                        { id: 'avatarStudio.editor.swatches.moveEarlier' },
                        { hex: swatch },
                      )}
                      onClick={() =>
                        onReorder(
                          moveSwatch(swatches, index, 'earlier'),
                          swatch,
                          'earlier',
                        )
                      }
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === swatches.length - 1}
                      aria-label={formatMessage(
                        { id: 'avatarStudio.editor.swatches.moveLater' },
                        { hex: swatch },
                      )}
                      onClick={() =>
                        onReorder(
                          moveSwatch(swatches, index, 'later'),
                          swatch,
                          'later',
                        )
                      }
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {onSwatchAction && (
                  <IconButton
                    size="small"
                    aria-label={formatMessage(
                      { id: 'avatarStudio.actions.swatchMenuAria' },
                      { hex: swatch },
                    )}
                    onClick={(e) =>
                      setMenu({ el: e.currentTarget, hex: swatch })
                    }
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              <Box
                role="img"
                aria-label={formatMessage(
                  { id: 'avatarStudio.editor.swatches.aria' },
                  { hex: swatch },
                )}
                sx={{ height: 72, borderRadius: 1, bgcolor: `#${swatch}` }}
              />
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" translate="no" sx={{ flexGrow: 1 }}>
                  {swatch}
                </Typography>
                {withdrawn && (
                  <Tooltip
                    title={formatMessage({
                      id: 'avatarStudio.option.retiredExplained',
                    })}
                  >
                    <Chip
                      size="small"
                      tabIndex={0}
                      color="warning"
                      variant="outlined"
                      label={formatMessage({
                        id: 'avatarStudio.option.status.retired',
                      })}
                    />
                  </Tooltip>
                )}
                {unlocksAt !== null && (
                  <Tooltip
                    title={formatMessage(
                      { id: 'avatarStudio.option.unlocksAtExplained' },
                      { tier: unlocksAt },
                    )}
                  >
                    <Chip
                      size="small"
                      tabIndex={0}
                      icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />}
                      label={formatMessage(
                        { id: 'avatarStudio.option.tierBadge' },
                        { tier: unlocksAt },
                      )}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Paper>
        )
      })}

      <Menu
        anchorEl={menu?.el ?? null}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
      >
        {menu && isRetired(menu.hex) ? (
          <MenuItem onClick={() => choose('restore')}>
            {formatMessage({ id: 'avatarStudio.actions.restore' })}
          </MenuItem>
        ) : (
          <MenuItem onClick={() => choose('retire')}>
            {formatMessage({ id: 'avatarStudio.actions.retire' })}
          </MenuItem>
        )}
      </Menu>

      {onAdd && (
        <AddTile
          label={formatMessage({ id: 'avatarStudio.grid.addColour' })}
          height={120}
          onClick={onAdd}
        />
      )}
    </Box>
  )
}
